<template>
	<core-layout-content full>
		<core-layout-headline-and-actions>
			<template slot="headline">
				<div class="flex xs-middle">
					<!-- Back button -->
					<j-button
						size="small"
						type="secondary"
						@click="$router.push({ name: 'TemplatesList' })"
						class="mr2"
					>
						<crm-icon name="back" size="normal" color="c5" class="mr1" />
					</j-button>
					{{ computedName }}
				</div>
			</template>
			<template slot="actions">
				<j-button @click="handleUpdate">{{ $t("update") }}</j-button>
			</template>
		</core-layout-headline-and-actions>

		<div class="flex xs-12">
			<!-- Left/Monaco Editor -->
			<div class="xs-9 pr4">
				<core-monaco-editor
					language="json"
					v-model="editorModel"
					style="min-height: 100vh; "
				/>
			</div>

			<!-- Right -->
			<div class="xs-3 b1 bl1 bc7 bg6--d2 p4" style="min-height: 100vh;">
				<!-- Name -->
				<p class="fs3 c5 mb2">{{ $t("name") }}</p>
				<j-base-input-text size="xsmall" class="mb2" v-model="name" />

				<!-- Description -->
				<p class="fs3 c5 mb2">{{ $t("description") }}</p>
				<j-base-input-long-text size="xsmall" class="mb6" v-model="description" />

				<!-- Dynamic values -->
				<div class="flex xs-12 xs-between xs-middle mb2">
					<span class="fs3 c5">{{ `${$t("dynamic_values")}: ${computedListCount}` }}</span>
					<j-button size="small" @click="addDynamicValue">{{ $t("add") }}</j-button>
				</div>

				<div class="b1 bc7 mb6" style="height: 300px; overflow-y: scroll;">
					<div
						v-for="(item, i) in computedList"
						@click="onSelect(item.valueId)"
						:key="i"
						class="bb1 bc7 xs-between xs-middle pt4 pb4 pr2 pl2 fs3 flex xs-12"
						style="height: 50px; overflow-y: hidden;"
						:class="item.valueId === selectedId ? 'bg2 c5' : 'bg5'"
					>
						<!-- <pre><code>{{item.valueJsonString}}</code></pre> -->
						---
						<crm-icon
							name="delete"
							size="small"
							color="cError"
							@click.native="deleteDynamicValue(item.valueId)"
						/>
					</div>

					<!-- If no data -->
					<div
						v-if="!computedList || computedList.length === 0"
						class="xs-12 flex xs-center xs-middle p4 fs3 c5 mt4"
					>{{ $t("errors.no_data") }}</div>
				</div>
			</div>
		</div>
	</core-layout-content>
</template>

<script>
import CrmIcon from "@/components/CrmIcon"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent.vue"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions.vue"
import JButton from "@/components/JButton"
import JCard from "@/components/JCard/"
import CoreMonacoEditor from "@/modules/core/components/CoreMonacoEditor"
import JBaseInputText from "@/components/JBaseInputText"
import JBaseInputLongText from "@/components/JBaseInputLongText"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		CrmIcon,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		JButton,
		JCard,
		CoreMonacoEditor,
		JBaseInputText,
		JBaseInputLongText
	},

	data() {
		return {
			fetchedTemplate: null,
			selectedId: null,
			editorModel: null,
			name: null,
			description: null
		}
	},

	methods: {
		async fetchDynamicTemplate() {
			const query = gql`
				query templates($where: templateWhereInput!) {
					templates(where: $where) {
						list {
							id
							name
							description
							... on dynamicTemplate {
								dynamicValues {
									valueId
									valueJsonString
								}
							}
						}
					}
				}
			`
			const variables = {
				where: {
					id: this.$route.params.id
				}
			}

			const res = await api({ query, variables })
			this.fetchedTemplate = res?.data?.templates?.list?.[0]
		},

		onSelect(id) {
			this.selectedId = id
		},

		async addDynamicValue() {
			const query = gql`
				mutation addDynamicValueToTemplate(
					$templateId: String!
					$input: addDynamicValueToTemplateInput!
				) {
					addDynamicValueToTemplate(templateId: $templateId, input: $input) {
						status
						errors
					}
				}
			`
			const variables = {
				templateId: this.$route.params.id,
				input: { valueJsonString: "{}" } // default value
			}

			const res = await api({ query, variables })

			if (res?.data?.addDynamicValueToTemplate?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.addDynamicValueToTemplate?.errors ||
							res?.data?.addDynamicValueToTemplate?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("dynamic_value_added")
					}
				})

			this.fetchDynamicTemplate()
		},

		async updateDynamicValue() {
			const query = gql`
				mutation updateDynamicValueOfTemplate(
					$templateId: String!
					$valueId: String!
					$input: updateDynamicValueOfTemplateInput!
				) {
					updateDynamicValueOfTemplate(
						templateId: $templateId
						valueId: $valueId
						input: $input
					) {
						status
						errors
					}
				}
			`
			const variables = {
				templateId: this.$route.params.id,
				valueId: this.selectedId,
				input: { valueJsonString: JSON.stringify(this.editorModel) }
			}

			const res = await api({ query, variables })

			if (res?.data?.updateDynamicValueOfTemplate?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.updateDynamicValueOfTemplate?.errors ||
							res?.data?.updateDynamicValueOfTemplate?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			}
		},

		async updateTemplate() {
			const query = gql`
				mutation updateTemplate(
					$templateId: String!
					$input: updateDynamicTemplateInput!
				) {
					updateDynamicTemplate(templateId: $templateId, input: $input) {
						status
						errors
					}
				}
			`
			const variables = {
				templateId: this.$route.params.id,
				input: { name: this.name, description: this.description }
			}

			const res = await api({ query, variables })

			if (res?.data?.updateDynamicTemplate?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.updateDynamicTemplate?.errors ||
							res?.data?.updateDynamicTemplate?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			}
		},

		async deleteDynamicValue(valueId) {
			const query = gql`
				mutation removeDynamicValue($templateId: String!, $valueId: String!) {
					removeDynamicValueFromTemplate(
						templateId: $templateId
						valueId: $valueId
					) {
						status
						errors
					}
				}
			`
			const variables = {
				templateId: this.$route.params.id,
				valueId
			}

			const res = await api({ query, variables })

			if (res?.data?.removeDynamicValueFromTemplate?.status !== "success") {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text:
							res?.errors ||
							res?.data?.removeDynamicValueFromTemplate?.errors ||
							res?.data?.removeDynamicValueFromTemplate?.errors_2.message ||
							"Oops ! An error occured."
					}
				})
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("dynamic_value_deleted")
					}
				})

			this.fetchDynamicTemplate()
		},

		async handleUpdate() {
			if (this.selectedId !== null) {
				await this.updateDynamicValue()
			}
			await this.updateTemplate()
			this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("dynamic_template_updated")
				}
			})
			this.fetchDynamicTemplate()
		}
	},

	computed: {
		computedList() {
			return this.fetchedTemplate?.dynamicValues?.reverse()
		},

		selectedItem() {
			return this.fetchedTemplate?.dynamicValues?.find(
				item => item.valueId === this.selectedId
			)
		},

		computedName() {
			return this.fetchedTemplate?.name
		},

		computedListCount() {
			return this.fetchedTemplate?.dynamicValues?.length || 0
		}
	},

	async created() {
		await this.fetchDynamicTemplate()
		this.selectedId = this.fetchedTemplate?.dynamicValues?.[0]?.valueId || null // autoselect first dynamicValue item
	},

	watch: {
		selectedItem: {
			handler() {
				this.editorModel = this.selectedItem?.valueJsonString || null
			},
			immediate: true,
			deep: true
		},

		fetchedTemplate: {
			handler() {
				this.name = this.fetchedTemplate?.name
				this.description = this.fetchedTemplate?.description
			},
			immediate: true,
			deep: true
		}
	}
}
</script>
